/**
 * @ngdoc service
 * @module flowingly.runner
 * @name runnerSpinInterceptor
 *
 * @description A service for intercepting requests / responses and starting / stopping spinner
 */
import angular, {
  IHttpInterceptor,
  IHttpPromiseCallbackArg,
  IQService,
  IRequestConfig
} from 'angular';
import { IUsSpinnerService } from '@Shared.Angular/@types/angular.spinner';

runnerSpinInterceptor.$inject = ['$q', 'usSpinnerService'];

function runnerSpinInterceptor(
  $q: IQService,
  usSpinnerService: IUsSpinnerService
): IHttpInterceptor {
  let activeRequestId = 0;
  const activeRequestIds = new Set<number>();

  //API
  const service = {
    request: request,
    requestError: requestError,
    response: response,
    responseError: responseError
  };

  return service;

  //////////// Public API Methods
  function request(config: IRequestConfig) {
    if (!shouldTrackRequest(config)) {
      return config;
    }
    activeRequestId += 1;
    config.spinnerRequestId = activeRequestId;
    activeRequestIds.add(activeRequestId);
    usSpinnerService.spin(MAIN_SPINNER_ID);
    return config;
  }

  function requestError(rejection) {
    if (shouldTrackRequest(rejection?.config)) {
      clearRequestTracking(rejection.config);
    }
    //need to manually reject the promise here, else the error handlers of $http.post etc are never hit!
    return $q.reject(rejection);
  }

  function response<T>(response: IHttpPromiseCallbackArg<T>) {
    if (shouldTrackRequest(response.config)) {
      clearRequestTracking(response.config);
    }
    return response;
  }

  function responseError(rejection) {
    if (shouldTrackRequest(rejection?.config)) {
      clearRequestTracking(rejection.config);
    }
    //need to manually reject the promise here, else the error handlers of $http.post etc are never hit!
    return $q.reject(rejection);
  }

  function shouldTrackRequest(config?: IRequestConfig) {
    if (!config) {
      return false;
    }
    const isApiRequest =
      config.url.indexOf('api') > -1 || config.url.indexOf('odata') > -1;
    if (!isApiRequest) {
      return false;
    }
    if (
      config.url.indexOf('files') > -1 &&
      config.method === 'GET' &&
      config.url.indexOf('files/download') === -1
    ) {
      return false;
    }
    if (config.url.indexOf('log/audit') > -1) {
      return false;
    }
    if (config.noSpinner) {
      return false;
    }
    return true;
  }

  function clearRequestTracking(config: IRequestConfig) {
    const requestId = config.spinnerRequestId;
    if (typeof requestId === 'number') {
      activeRequestIds.delete(requestId);
    }
    if (activeRequestIds.size === 0) {
      usSpinnerService.stop(MAIN_SPINNER_ID);
    }
  }
}

export const MAIN_SPINNER_ID = 'spinner-main';

angular
  .module('flowingly.runner')
  .factory('runnerSpinInterceptor', runnerSpinInterceptor);
